<template>
  <main class="wrapper-content">
    <template v-if="!isLoading">
      <h1>{{ header }}</h1>
      <b-row>
        <LmTextInput
          class="col-md-6 col-12"
          placeholder="Nombre"
          label="Nombre"
          v-model="$v.name.$model"
          :v="v('name')"
        />
        <LmColorPicker
          class="col-md-6 col-12"
          label="Color"
          :swatches="swatches"
          v-model="$v.color.$model"
          :v="v('color')"
        />
        <b-col md="12" class="mb-4">
          <div>
            <h3>Privilegios que conforman el Rol</h3>
            <div class="d-flex flex-wrap mb-3" style="gap: 1rem" v-if="privileges.length">
              <span
                class="p-2 border rounded-lg d-flex align-items-center justify-content-between"
                style="gap: 1rem"
                v-for="(item, index) in privileges"
                :key="index"
              >
                <span>{{ item.name }}</span>
                <span @click="onRemove(index)">
                  <i class="mdi mdi-close-circle-outline clickable" style="font-size: 1.3rem"></i>
                </span>
              </span>
            </div>
            <div v-else>
              <p>Agrega privilegios para poder crear un nuevo rol.</p>
            </div>
            <LmButton
              buttonClass="btn-info"
              value="Agregar privilegios"
              @click="$bvModal.show('privileges-datatable-modal')"
            />
          </div>
        </b-col>
        <LmButton
          class="col-auto"
          buttonClass="btn-secondary"
          :value="$t('cancel')"
          @click="onCancel"
        />
        <LmButton
          class="col-auto"
          buttonClass="btn-primary"
          :value="$t('save')"
          :disabled="!privileges.length"
          @click="onSubmit"
        />
        <LmButton
          class="col-auto"
          action="delete"
          buttonClass="btn-danger"
          :value="$t('delete')"
          v-if="isOnEditMode"
          @click="onDelete"
        />
      </b-row>
    </template>
    <!-- loading state -->
    <b-row class="mt-4" v-else>
      <b-col><b-skeleton type="input"></b-skeleton></b-col>
      <b-col><b-skeleton type="input"></b-skeleton></b-col>
      <b-col md="12" class="my-4">
        <div>
          <div class="d-flex flex-wrap mb-3" style="gap: 1rem" v-if="privileges.length">
            <span
              class="p-2 border rounded-lg d-flex align-items-center justify-content-between"
              style="gap: 1rem"
              v-for="(item, index) in 4"
              :key="index"
            >
              <b-skeleton type="avatar"></b-skeleton>
              <b-skeleton width="100px"></b-skeleton>
            </span>
          </div>
          <b-skeleton type="button" width="150px"></b-skeleton>
        </div>
      </b-col>
    </b-row>
    <b-modal
      id="privileges-datatable-modal"
      size="xl"
      @ok="onConfirmPrivileges"
      :cancel-title="$t('cancel')"
      :ok-title="$t('confirm')"
    >
      <LmDataTable
        @onSelect="onSelectPrivileges"
        slug="listado-de-privilegios-para-roles"
        hideButtons
      />
    </b-modal>
  </main>
</template>

<script>
// Components
import LmTextInput from '@/components/form/elements/LmTextInput'
import LmColorPicker from '@/components/form/elements/LmColorPicker'
import LmButton from '@/components/form/elements/LmButton'
import LmDataTable from '@/components/lmDataTable/table'

// Utils
import commonsApi from '@/api/commons'
import lmToasts from '@/mixins/lmToasts'
import _ from 'lodash'
import { required } from 'vuelidate/lib/validators'

export default {
  mixins: [lmToasts],
  components: {
    LmTextInput,
    LmColorPicker,
    LmButton,
    LmDataTable
  },
  props: {
    isOnEditMode: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isLoading: false,
      name: '',
      color: '',
      privileges: [],
      privilegesToAdd: [],
      swatches: [
        ['#F6648B', '#F493A7', '#F891A6', '#FFCCD5'],
        ['#a27bff', '#b99cff', '#d0bdff', '#e8deff'],
        ['#74ebe3', '#96f0ea', '#b9f5f1', '#dcfaf8'],
        ['#ffb748', '#ffc976', '#ffdba3', '#ffedd1']
      ]
    }
  },
  computed: {
    header () {
      return this.isOnEditMode ? `Editar ${this.name}` : 'Nuevo Rol'
    }
  },
  validations: {
    name: { required },
    color: { required }
  },
  methods: {
    v (position) {
      return _.get(_.cloneDeep(this.$v), position)
    },
    validateForm (validation) {
      validation.$touch()
      if (validation.$error) {
        this.lmToastDangerMessage(this.$t('form_with_errors'), 'Error')
        throw new Error('validation error')
      } else {
        if (validation.$pending || validation.$error) {
          throw new Error('validation error')
        }
      }
    },
    async onSubmit () {
      try {
        await this.validateForm(this.$v)
        if (this.isOnEditMode) {
          await commonsApi.axiosPutWrapper(`/roles/${this.$route.params.id}`, {
            name: this.name,
            color: this.color,
            privileges: this.privileges
          })
        } else {
          const res = await commonsApi.axiosPostWrapper('/roles', {
            name: this.name,
            color: this.color,
            privileges: this.privileges
          })
          this.$router.replace(`/roles/editar/${res.data._id}`)
        }
        this.lmToastSuccessMessage(
          `Se guardó correctamente el rol ${this.name}`,
          'Acción realizada con éxito'
        )
      } catch (error) {
        console.log(error)
      }
    },
    onCancel () {
      this.$router.go(-1)
    },
    async onDelete () {
      try {
        await commonsApi.axiosDeleteWrapper(`/roles/delete/${this.$route.params.id}`)
        this.lmToastSuccessMessage(
          `Se eliminó correctamente el rol ${this.name}`,
          'Acción realizada con éxito'
        )
        this.onCancel()
      } catch (error) {
        console.log(error)
      }
    },
    onRemove (index) {
      this.privileges.splice(index, 1)
    },
    async onSelectPrivileges (event) {
      let privileges = _.cloneDeep(this.privileges)

      privileges.push(event.map(el => {
        return {
          id: el.id,
          name: el['privilege-name']
        }
      }))

      privileges = privileges.flat()

      this.privilegesToAdd = [...new Map(privileges.map(item => [item.id, item])).values()]
    },
    onConfirmPrivileges () {
      this.privileges = this.privilegesToAdd
      this.privilegesToAdd = []
    },
    async loadData () {
      const res = await commonsApi.axiosGetWrapper(`/roles/${this.$route.params.id}`)
      this.name = res.data.name
      this.color = res.data.color
      this.privileges = res.data.privileges.map(el => {
        return {
          id: el._id,
          name: el.name
        }
      })
    }
  },
  async created () {
    try {
      this.isLoading = true
      if (this.isOnEditMode) {
        await this.loadData()
      }
    } catch (error) {
      console.log(error)
    } finally {
      this.isLoading = false
    }
  }
}
</script>
